import React, { useState, useEffect } from "react";
import Select, { GroupBase } from "react-select";

interface SelectOption {
    value: string | number | null;
    label: string;
    display_name?: string;
    image_base64?: string;
    disabled?: boolean;
}

interface CustomSelectProps {
    options: SelectOption[];
    multi?: boolean;
    name?: string;
    label?: string;
    title?: string;
    placeholderOnly?: string;
    defaultValue?: any;
    value?: any;
    required?: boolean;
    error?: boolean;
    errorMessage?: string;
    onChange?: (value: any) => void;
    isDisabled?: boolean;
    closeOnSelect?: boolean;
    isClearable?: boolean;
}

const toDataUrl = (b64?: string) => {
    if (!b64) return "";
    return b64.startsWith("data:")
        ? b64
        : `data:image/webp;base64,${b64}`;
};

const CustomSelect: React.FC<CustomSelectProps> = ({
                                                       title,
                                                       placeholderOnly,
                                                       options,
                                                       required,
                                                       error,
                                                       errorMessage,
                                                       multi = false,
                                                       onChange,
                                                       value,
                                                       defaultValue,
                                                       isDisabled,
                                                       closeOnSelect = true,
                                                       isClearable = false,
                                                       ...props
                                                   }) => {
    const [internalValue, setInternalValue] = useState(value !== undefined ? value : defaultValue);
    // Tracks a failed required-validation so we can show an inline error. The
    // browser's native bubble can't anchor to the visually-hidden input below,
    // so we surface the message ourselves instead.
    const [invalid, setInvalid] = useState(false);
    const showError = invalid || !!error;

    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value]);

    const safeValue = multi
        ? Array.isArray(value)
            ? options.filter((option) => value.includes(option.value))
            : []
        : options.find((option) => option.value === value) || null;

    const safeDefaultValue = multi
        ? Array.isArray(defaultValue)
            ? options.filter((option) => defaultValue.includes(option.value))
            : []
        : options.find((option) => option.value === defaultValue) || null;

    const customStyles = {
        control: (base: any, state: any) => ({
            ...base,
            fontSize: "0.875rem",
            fontFamily: "sans-serif",
            borderColor: state.isFocused ? "red" : showError ? "#ef4444" : base.borderColor,
            boxShadow: state.isFocused ? "0 0 0 1px red" : base.boxShadow,
            "&:hover": { borderColor: state.isFocused ? "red" : showError ? "#ef4444" : base.borderColor },
        }),
        option: (base: any, state: any) => ({
            ...base,
            fontSize: "0.875rem",
            fontFamily: "sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: state.isSelected ? "#FFFFFF" : "#000000",
            backgroundColor: state.isSelected ? "red" : state.isFocused ? "darkgray" : "#FFFFFF",
            // Keep thumbnails aligned by preventing excessive padding shifts
            paddingTop: 6,
            paddingBottom: 6,
        }),
        input: (base: any) => ({
            ...base,
            fontSize: "0.875rem",
            fontFamily: "sans-serif",
            outline: "none",
            boxShadow: "none",
            "&::placeholder": { color: "transparent" },
        }),
        multiValue: (base: any) => ({
            ...base,
            alignItems: "center",
            gap: 6,
            paddingLeft: 2,
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: 0,
        }),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 9999,
        }),
        menu: (base: any) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const formatOptionLabel = (
        opt: SelectOption,
        { context }: { context: "menu" | "value" }
    ) => {
        const name = opt.display_name ?? opt.label ?? String(opt.value ?? "");
        const src = toDataUrl(opt.image_base64);
        const size = context === "menu" ? 65 : 28;

        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {src && (
                    <img
                        src={src}
                        alt={name}
                        width={size}
                        height={size}
                        loading="lazy"
                        style={{
                            width: size,
                            height: size,
                            objectFit: "cover",
                            borderRadius: 4,
                            border: "1px solid #e5e7eb",
                            flex: "0 0 auto",
                        }}
                    />
                )}
                <span
                    style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    title={name}
                >
          {name}
        </span>
            </div>
        );
    };

    const handleChange = (selectedOptions: any) => {
        let newValue;
        if (multi) {
            const selectedValues = selectedOptions
                ? selectedOptions.map((opt: SelectOption) => opt.value)
                : [];
            newValue = selectedValues;
            onChange && onChange(selectedValues);
        } else {
            newValue = selectedOptions ? selectedOptions.value : null;
            onChange && onChange(newValue);
        }
        setInternalValue(newValue);
        const hasValue = Array.isArray(newValue) ? newValue.length > 0 : newValue !== null && newValue !== undefined;
        if (hasValue) setInvalid(false);
    };

    const getHiddenInputValue = () => {
        const currentValue = value !== undefined ? value : internalValue;

        if (multi) {
            return Array.isArray(currentValue) && currentValue.length > 0
                ? JSON.stringify(currentValue)
                : "";
        } else {
            return currentValue !== null && currentValue !== undefined ? String(currentValue) : "";
        }
    };

    return (
        <div className="w-full">
            {title && (
                <label className="block text-sm font-medium text-gray-700">
                    {title}
                    {required && <span className="text-red-500"> *</span>}
                </label>
            )}
            <Select<SelectOption, boolean, GroupBase<SelectOption>>
                {...props}
                isDisabled={isDisabled}
                onChange={handleChange as any}
                options={options}
                styles={customStyles}
                placeholder={`Select ${title ? title : placeholderOnly}...`}
                isMulti={multi}
                value={value !== undefined ? safeValue : undefined}
                defaultValue={value === undefined ? safeDefaultValue : undefined}
                closeMenuOnSelect={closeOnSelect}
                isClearable={isClearable}
                hideSelectedOptions={false}
                className="mt-1"
                menuPortalTarget={document.body}
                formatOptionLabel={formatOptionLabel}
            />
            {/* Hidden input for form validation when required. Kept at 1px (not
                display:none / zero-size) so the browser treats it as focusable and
                actually blocks submission; onInvalid drives our inline message. */}
            {required && props.name && (
                <input
                    type="text"
                    name={props.name}
                    value={getHiddenInputValue()}
                    onChange={() => {}} // Controlled input, value managed by react-select
                    onInvalid={(e) => {
                        // Suppress the native bubble (it can't anchor to this
                        // hidden input) and show our inline error instead.
                        e.preventDefault();
                        setInvalid(true);
                    }}
                    required={required}
                    style={{ position: 'absolute', opacity: 0, height: 1, width: 1, bottom: 0, left: '50%' }}
                    tabIndex={-1}
                    aria-hidden="true"
                />
            )}
            {showError && (
                <p className="mt-1 text-sm text-red-600">
                    {errorMessage ?? (title ? `${title} is required` : "This field is required")}
                </p>
            )}
        </div>
    );
};

export default CustomSelect;
